import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { QokrAvatar } from "../QokrPrimitives/QokrPrimitives";
import "../QokrPrimitives/_qokr-avatar.scss";
import "./_qokr-select.scss";

// ═══════════════════════════════════════════════════════════════════════════
// QOKR's own single select — the forms' dropdown (owner, person, linked
// objective), in the look of the scorecard's pickers rather than the
// react-select default. The menu is portalled to <body> and positioned
// against the trigger, so it is never clipped by a scrolling dialog, and it
// opens upwards when there's no room below.
//
// `options`: [{ value, label, person? }] — `person` ({ name }) adds an
// avatar. `value` matches by String(). `clearable` adds an ×. Searchable from
// SEARCH_FROM options. Shared, byte-identical, by the CEO and coach apps.
// ═══════════════════════════════════════════════════════════════════════════

const SEARCH_FROM = 8;
const MENU_MAX = 280;
const GAP = 6;

const QokrSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select",
  clearable = false,
  disabled = false,
  icon = null, // a font-awesome class shown before the value
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected =
    value == null
      ? null
      : options.find((o) => String(o.value) === String(value)) || null;
  const searchable = options.length >= SEARCH_FROM;
  const q = query.trim().toLowerCase();
  const shown = q
    ? options.filter((o) => String(o.label).toLowerCase().includes(q))
    : options;

  const place = () => {
    const node = triggerRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - GAP - 12;
    const above = r.top - GAP - 12;
    const up = below < Math.min(MENU_MAX, 220) && above > below;
    setRect({
      left: r.left,
      width: r.width,
      top: up ? null : r.bottom + GAP,
      bottom: up ? window.innerHeight - r.top + GAP : null,
      maxHeight: Math.max(140, Math.min(MENU_MAX, up ? above : below)),
    });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    if (searchable) searchRef.current?.focus();
    const onDown = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      )
        return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      // Close the menu, not the dialog around it.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onMove = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      place();
    };
    document.addEventListener("mousedown", onDown);
    // On window, capture: ahead of EbDialog's own Escape listener (document,
    // capture), so Escape closes the menu and not the dialog under it.
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, searchable]); // eslint-disable-line

  const pick = (option) => {
    setOpen(false);
    onChange?.(option ? option.value : null);
    triggerRef.current?.focus();
  };

  return (
    <div
      className={`qokr-select${disabled ? " is-disabled" : ""}${
        clearable && selected && !disabled ? " is-clearable" : ""
      }`}
    >
      <button
        type="button"
        ref={triggerRef}
        className={`qokr-select__trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
      >
        {selected?.person ? (
          <QokrAvatar person={selected.person} size="sm" />
        ) : icon ? (
          <span className="qokr-select__lead" aria-hidden="true">
            <i className={icon} />
          </span>
        ) : null}
        <span
          className={`qokr-select__value${selected ? "" : " is-placeholder"}`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <i
          className={`fa-solid fa-chevron-down qokr-select__chev${open ? " is-open" : ""}`}
          aria-hidden="true"
        />
      </button>
      {clearable && selected && !disabled ? (
        <button
          type="button"
          className="qokr-select__clear"
          aria-label="Clear"
          onClick={() => pick(null)}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      ) : null}

      {open && rect
        ? ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="qokr-select__menu"
              style={{
                left: rect.left,
                width: rect.width,
                top: rect.top ?? "auto",
                bottom: rect.bottom ?? "auto",
                maxHeight: rect.maxHeight,
              }}
            >
              {searchable ? (
                <div className="qokr-select__search">
                  <i
                    className="fa-solid fa-magnifying-glass"
                    aria-hidden="true"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    placeholder="Search"
                    aria-label="Search"
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>
              ) : null}
              <div className="qokr-select__list" role="listbox">
                {shown.map((option) => {
                  const isSelected =
                    selected && String(selected.value) === String(option.value);
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected={Boolean(isSelected)}
                      key={option.value}
                      className={`qokr-select__option${isSelected ? " is-selected" : ""}`}
                      onClick={() => pick(option)}
                    >
                      {option.person ? (
                        <QokrAvatar person={option.person} size="sm" />
                      ) : null}
                      <span className="qokr-select__option-label">
                        {option.label}
                      </span>
                      {isSelected ? (
                        <i
                          className="fa-solid fa-check qokr-select__check"
                          aria-hidden="true"
                        />
                      ) : null}
                    </button>
                  );
                })}
                {!shown.length ? (
                  <div className="qokr-select__empty">
                    {q ? "Nothing matches." : "Nothing to choose from."}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default QokrSelect;
